import { ref, Ref, computed, onMounted, onUnmounted, watch } from 'vue';
import store from '@/store';
import { useCanvasEvents } from './useCanvasEvents';
import { useElementSelection } from './useElementSelection';

import type { CanvasElement } from '@/typings/customilyElements/canvasElements';
import type { ElementsGroup } from '@/typings/customilyElements/elementsGroup';
import type { ElementsPageEps } from '@/typings/customilyElements/elementsPageEps';
import type { ImportLayersEventData } from '@/typings/layers/layers';
import type { LayersProps } from '../types';

export function useLayers(props: LayersProps, emit: (event: 'import-layers', data: ImportLayersEventData) => void) {
    const canvasElements: Ref<CanvasElement[]> = ref([]);
    const listPageEps: Ref<ElementsPageEps[]> = ref([]);
    const groups: Ref<ElementsGroup[]> = ref([]);
    const auxCanvasElements: Ref<CanvasElement[]> = ref([]);

    const settings = computed(() => {
        return store.state.storeLayers?.settings;
    });

    const isUpdatingTree = computed(() => store.state.storeLayers?.isUpdatingTree);

    const { setCanvasEventListeners, removeCanvasEventListeners, initializeLayers } = useCanvasEvents(props);

    const {
        setActiveElement,
        singleGroupSelection,
        initGroupSelectionFromPageEps,
        moveElementForward,
        moveElementBackward,
        bringElementToFront,
        sendElementToBack
    } = useElementSelection(props, listPageEps, groups);

    const importLayers = (data: ImportLayersEventData) => {
        emit('import-layers', data);
    };

    const initialize = () => {
        store.dispatch('storeLayers/setCanvas', props.canvas);
        store.dispatch('storeLayers/setTemplate', props.template);
    };

    watch(
        () => props.settings,
        () => {
            store.dispatch('storeLayers/setSettings', props.settings);
        },
        { immediate: true, deep: true }
    );

    watch(
        () => props.flags,
        () => {
            store.dispatch('storeLayers/setFlags', props.flags);
        },
        { immediate: true, deep: true }
    );

    watch(
        () => props.canvas,
        () => {
            try {
                initialize();
                setCanvasEventListeners();
            } catch (error) {
                console.error('Error updating canvas', error);
            }
        }
    );

    watch(
        () => props.auxCanvas,
        () => {
            try {
                if (!props.auxCanvas) {
                    return;
                }
                setCanvasEventListeners(props.auxCanvas);
            } catch (error) {
                console.error('Error updating auxCanvas', error);
            }
        }
    );

    onMounted(() => {
        initialize();
        setCanvasEventListeners();
        if (props.auxCanvas) {
            setCanvasEventListeners(props.auxCanvas);
        }
    });

    onUnmounted(() => {
        removeCanvasEventListeners();
        if (props.auxCanvas) {
            removeCanvasEventListeners(props.auxCanvas);
        }
    });

    return {
        // State
        canvasElements,
        listPageEps,
        groups,
        auxCanvasElements,
        isUpdatingTree,
        settings,

        // Methods
        setActiveElement,
        singleGroupSelection,
        initGroupSelectionFromPageEps,
        importLayers,
        initialize,
        refreshLayerPanel: initializeLayers,
        moveElementForward,
        moveElementBackward,
        bringElementToFront,
        sendElementToBack
    };
}
